import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import * as devConfig from "../../app/configs/devConfig";
import {
  passwordProblem,
  saveWelcome,
  teamLine,
} from "../onboarding/journey/journey.utils";
import {
  JOURNEY_BUTTON,
  JourneyError,
  JourneyLayout,
  JourneyTitle,
  LockedEmail,
  PasswordFields,
} from "../onboarding/journey/JourneyParts";
import {
  acceptFailureText,
  EXPIRED_INVITE_TEXT,
  INVALID_INVITE_TEXT,
  PublicInvite,
  readInvite,
} from "./userInvite.utils";

type PageState =
  | { kind: "loading" }
  | { kind: "invalid"; text: string }
  | { kind: "form"; invite: PublicInvite }
  | { kind: "done"; invite: PublicInvite };

/**
 * Team invitation page /user-invitations/:id (redesigned 2026-10-25, owner): "Welcome, Sarah",
 * password with live checks, then "Your account is ready" + Sign in. Remembers (this browser) to
 * show the welcome window with confetti after the first sign-in.
 */
const UserInvite = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Join your team | ProCharted";
    (async () => {
      try {
        const response = await fetch(
          `${devConfig.getServerBaseUrlForEnv()}/api/user-invitations/${id}`,
          {
            headers: { Accept: "application/json" },
          },
        );
        const invite = response.ok ? readInvite(await response.json()) : null;
        if (!invite) setState({ kind: "invalid", text: INVALID_INVITE_TEXT });
        else if (invite.inviteAccepted) navigate("/");
        else if (invite.expired)
          setState({ kind: "invalid", text: EXPIRED_INVITE_TEXT });
        else setState({ kind: "form", invite });
      } catch {
        setState({ kind: "invalid", text: INVALID_INVITE_TEXT });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const problem = passwordProblem(password, confirm);

  const accept = async () => {
    if (state.kind !== "form") return;
    setTried(true);
    if (problem || saving) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${devConfig.getServerBaseUrlForEnv()}/api/user-invitations/mark-invite-accepted/${id}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          method: "PUT",
          // The server takes names, firm, access level and email from the stored invitation.
          body: JSON.stringify({
            firstName: state.invite.firstName,
            lastName: state.invite.lastName,
            password,
          }),
        },
      );
      if (response.ok) {
        saveWelcome({
          kind: "member",
          email: state.invite.email || "",
          firmName: state.invite.firmName || "your firm",
          at: Date.now(),
        });
        setState({ kind: "done", invite: state.invite });
      } else if (response.status === 410) {
        setState({ kind: "invalid", text: EXPIRED_INVITE_TEXT });
      } else {
        setError(acceptFailureText(await response.text().catch(() => "")));
      }
    } catch {
      setError(acceptFailureText(""));
    } finally {
      setSaving(false);
    }
  };

  if (state.kind === "loading") {
    return (
      <JourneyLayout side={null}>
        <div className={"text-center text-[#8A90A2]"}>
          Loading your invitation…
        </div>
      </JourneyLayout>
    );
  }

  if (state.kind === "invalid") {
    return (
      <JourneyLayout
        side={
          <SideText
            title={"Let's get you in."}
            text={"Invitations keep your firm's data safe."}
          />
        }>
        <JourneyTitle title={"This link can't be used"} text={state.text} />
        <button
          type={"button"}
          className={JOURNEY_BUTTON}
          onClick={() => navigate("/")}>
          I already have an account: sign in
        </button>
      </JourneyLayout>
    );
  }

  const invite = state.invite;
  const firm = invite.firmName || "your firm";

  if (state.kind === "done") {
    return (
      <JourneyLayout
        side={
          <SideText
            title={"Nearly there."}
            text={`One more click and you're in ${firm}.`}
          />
        }>
        <div className={"text-center"}>
          <div
            className={
              "mx-auto mb-4 flex h-[76px] w-[76px] items-center justify-center rounded-full bg-[#E3F6EC] text-4xl text-[#1E9E5A]"
            }>
            ✓
          </div>
          <JourneyTitle
            step={"Step 2 of 2 · Sign in"}
            title={"Your account is ready"}
            text={
              <>
                Sign in with <b className={"text-[#1F2433]"}>{invite.email}</b>{" "}
                and the password you just chose.
              </>
            }
          />
          <button
            type={"button"}
            className={JOURNEY_BUTTON}
            onClick={() => navigate("/")}>
            Sign in to {firm} →
          </button>
        </div>
      </JourneyLayout>
    );
  }

  return (
    <JourneyLayout side={<InviteSide invite={invite} />}>
      <JourneyTitle
        step={"Step 1 of 2 · Create your account"}
        title={<>Welcome, {invite.firstName || "there"} 👋</>}
        text={"Choose the password you'll use to sign in."}
      />
      <LockedEmail email={invite.email || ""} />
      <PasswordFields
        password={password}
        confirm={confirm}
        onPassword={setPassword}
        onConfirm={setConfirm}
        onEnter={() => void accept()}
      />
      <JourneyError text={(tried && problem) || error} />
      <button
        type={"button"}
        className={`${JOURNEY_BUTTON} mt-6`}
        disabled={saving}
        onClick={() => void accept()}>
        {saving ? "Creating your account…" : "Create my account"}
      </button>
      <div className={"mt-3.5 text-center text-[12.5px] text-[#7A7F8C]"}>
        Already have an account?{" "}
        <button
          type={"button"}
          className={"font-bold text-[#6B4EFF]"}
          onClick={() => navigate("/")}>
          Sign in
        </button>
      </div>
    </JourneyLayout>
  );
};

const SideText = ({ title, text }: { title: string; text: string }) => (
  <>
    <div className={"text-[30px] font-extrabold leading-tight lg:text-[36px]"}>
      {title}
    </div>
    <div className={"mt-3.5 max-w-[380px] text-[15px] opacity-90"}>{text}</div>
  </>
);

const InviteSide = ({ invite }: { invite: PublicInvite }) => {
  const firm = invite.firmName || "your firm";
  const faces = invite.team || [];
  const extra = Math.max(0, (invite.teamCount || 0) - faces.length);
  return (
    <>
      {faces.length > 0 && (
        <div className={"flex"}>
          {faces.map((face, index) => (
            <span
              key={index}
              style={{ backgroundColor: face.color || "#1BA3E0" }}
              className={`flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-white text-sm font-extrabold ${
                index ? "-ml-3" : ""
              }`}>
              {face.initials}
            </span>
          ))}
          {extra > 0 && (
            <span
              className={
                "-ml-3 flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-white bg-white/25 text-[13px] font-extrabold"
              }>
              +{extra}
            </span>
          )}
        </div>
      )}
      <div
        className={
          "mt-5 max-w-[420px] text-[30px] font-extrabold leading-tight lg:text-[34px]"
        }>
        {teamLine(invite.teamCount || 0, firm)}
      </div>
      <div className={"mt-3.5 max-w-[380px] text-[15px] opacity-90"}>
        One place for your clients, work, deadlines and files.
      </div>
    </>
  );
};

export default UserInvite;
